import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Edit, DollarSign, TrendingUp, TrendingDown,
  BarChart3, Percent, ArrowUpRight, ArrowDownRight, Calendar, Wallet
} from "lucide-react";
import AccountsView from "./AccountsView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  store_id: "", week_start: "", revenue: "", projected_revenue: "",
  cogs: "", rent: "", labor_cost: "", other_expenses: "",
  projected_expenses: "", notes: "",
};

export default function Financials() {
  const [mainTab, setMainTab] = useState("weekly"); // "weekly" or "accounts"
  const [entries, setEntries] = useState([]);
  const [stores, setStores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterStore, setFilterStore] = useState("all");

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchEntries(); fetchSummary(); }, [filterStore]);

  const fetchData = async () => {
    try {
      const [eRes, sRes, sumRes] = await Promise.all([
        axios.get(`${API}/financials`),
        axios.get(`${API}/stores`),
        axios.get(`${API}/financials/summary/all`),
      ]);
      setEntries(eRes.data);
      setStores(sRes.data);
      setSummary(sumRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchEntries = async () => {
    try {
      const params = {};
      if (filterStore !== "all") params.store_id = filterStore;
      const res = await axios.get(`${API}/financials`, { params });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchSummary = async () => {
    try {
      const params = {};
      if (filterStore !== "all") params.store_id = filterStore;
      const res = await axios.get(`${API}/financials/summary/all`, { params });
      setSummary(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.store_id || !form.week_start) { toast.error("Store and week are required"); return; }
    const payload = {
      store_id: form.store_id,
      week_start: form.week_start,
      revenue: parseFloat(form.revenue) || 0,
      projected_revenue: parseFloat(form.projected_revenue) || 0,
      cogs: parseFloat(form.cogs) || 0,
      rent: parseFloat(form.rent) || 0,
      labor_cost: parseFloat(form.labor_cost) || 0,
      other_expenses: parseFloat(form.other_expenses) || 0,
      projected_expenses: parseFloat(form.projected_expenses) || 0,
      notes: form.notes,
    };
    try {
      if (editing) {
        await axios.put(`${API}/financials/${editing.id}`, payload);
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/financials`, payload);
        toast.success("Entry added");
      }
      closeDialog();
      fetchEntries();
      fetchSummary();
    } catch (e) { toast.error("Failed to save"); }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/financials/${id}`);
      toast.success("Entry deleted");
      fetchEntries();
      fetchSummary();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      store_id: entry.store_id,
      week_start: entry.week_start,
      revenue: entry.revenue?.toString() || "",
      projected_revenue: entry.projected_revenue?.toString() || "",
      cogs: entry.cogs?.toString() || "",
      rent: entry.rent?.toString() || "",
      labor_cost: entry.labor_cost?.toString() || "",
      other_expenses: entry.other_expenses?.toString() || "",
      projected_expenses: entry.projected_expenses?.toString() || "",
      notes: entry.notes || "",
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    const weekStart = monday.toISOString().split("T")[0];
    setForm({ ...emptyForm, week_start: weekStart, store_id: filterStore !== "all" ? filterStore : (stores[0]?.id || "") });
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setForm(emptyForm); setEditing(null); };

  const getStoreName = (id) => stores.find(s => s.id === id)?.name || "Unknown";

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const totalExpenses = (e) => (e.cogs || 0) + (e.rent || 0) + (e.labor_cost || 0) + (e.other_expenses || 0);

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="financials-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Financial Tracking</span>
          </div>
          <h1 data-testid="financials-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Financials
          </h1>
          <p className="text-base text-stone-500 mt-2">Track revenue, expenses, reports, and projections.</p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-lg mb-6 w-fit">
        <button
          data-testid="tab-weekly"
          onClick={() => setMainTab("weekly")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${mainTab === "weekly" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
        >
          <Calendar className="w-4 h-4" /> Weekly Tracking
        </button>
        <button
          data-testid="tab-accounts"
          onClick={() => setMainTab("accounts")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${mainTab === "accounts" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
        >
          <Wallet className="w-4 h-4" /> Accounts & Reports
        </button>
      </div>

      {/* Accounts Tab Content */}
      {mainTab === "accounts" && <AccountsView embedded />}

      {/* Weekly Tab Content */}
      {mainTab === "weekly" && (
        <>
          {/* Action Bar */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-entry-btn" onClick={openNew} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all duration-200 font-medium px-6 btn-scale">
                  <Plus className="w-4 h-4 mr-2" /> Add Week
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{editing ? "Edit Entry" : "Log Weekly Financials"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Store *</Label>
                      <Select value={form.store_id} onValueChange={v => setForm({ ...form, store_id: v })}>
                        <SelectTrigger data-testid="fin-store" className="mt-1"><SelectValue placeholder="Select store" /></SelectTrigger>
                        <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                  <Label className="text-xs font-medium text-stone-600">Week Starting *</Label>
                  <Input data-testid="fin-week" type="date" value={form.week_start} onChange={e => setForm({ ...form, week_start: e.target.value })} className="mt-1" />
                </div>
              </div>

              <p className="text-xs font-medium text-stone-500 uppercase tracking-wide pt-2">Revenue</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-600">Actual Revenue ($)</Label>
                  <Input data-testid="fin-revenue" type="number" step="0.01" value={form.revenue} onChange={e => setForm({ ...form, revenue: e.target.value })} placeholder="0" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Projected Revenue ($)</Label>
                  <Input data-testid="fin-proj-revenue" type="number" step="0.01" value={form.projected_revenue} onChange={e => setForm({ ...form, projected_revenue: e.target.value })} placeholder="0" className="mt-1" />
                </div>
              </div>

              <p className="text-xs font-medium text-stone-500 uppercase tracking-wide pt-2">Expenses</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-600">COGS ($)</Label>
                  <Input data-testid="fin-cogs" type="number" step="0.01" value={form.cogs} onChange={e => setForm({ ...form, cogs: e.target.value })} placeholder="0" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Rent ($)</Label>
                  <Input data-testid="fin-rent" type="number" step="0.01" value={form.rent} onChange={e => setForm({ ...form, rent: e.target.value })} placeholder="0" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Labor ($)</Label>
                  <Input data-testid="fin-labor" type="number" step="0.01" value={form.labor_cost} onChange={e => setForm({ ...form, labor_cost: e.target.value })} placeholder="0" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Other ($)</Label>
                  <Input data-testid="fin-other" type="number" step="0.01" value={form.other_expenses} onChange={e => setForm({ ...form, other_expenses: e.target.value })} placeholder="0" className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Projected Expenses ($)</Label>
                <Input data-testid="fin-proj-exp" type="number" step="0.01" value={form.projected_expenses} onChange={e => setForm({ ...form, projected_expenses: e.target.value })} placeholder="0" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-stone-600">Notes</Label>
                <textarea data-testid="fin-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Weekly notes..." className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400" />
              </div>
              <Button type="submit" data-testid="fin-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium mt-2">
                {editing ? "Update" : "Log Week"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6">
        <Select value={filterStore} onValueChange={setFilterStore}>
          <SelectTrigger data-testid="filter-fin-store" className="w-48 text-sm"><SelectValue placeholder="All stores" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="fin-total-revenue">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                <p className="text-xs text-stone-500">Total Revenue</p>
              </div>
              <p className="text-xl font-semibold text-stone-900">{fmt(summary.total_revenue)}</p>
              {summary.total_projected_revenue > 0 && (
                <p className="text-xs text-stone-400 mt-0.5">
                  {summary.total_revenue >= summary.total_projected_revenue ? (
                    <span className="text-emerald-600 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" /> vs {fmt(summary.total_projected_revenue)} proj</span>
                  ) : (
                    <span className="text-red-500 flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" /> vs {fmt(summary.total_projected_revenue)} proj</span>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="fin-total-expenses">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                <p className="text-xs text-stone-500">Total Expenses</p>
              </div>
              <p className="text-xl font-semibold text-stone-900">{fmt(summary.total_expenses)}</p>
            </CardContent>
          </Card>
          <Card className={`border rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] ${summary.net_profit >= 0 ? "bg-emerald-50/50 border-emerald-200/60" : "bg-red-50/50 border-red-200/60"}`} data-testid="fin-net-profit">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className={`w-3.5 h-3.5 ${summary.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`} />
                <p className="text-xs text-stone-500">Net Profit</p>
              </div>
              <p className={`text-xl font-semibold ${summary.net_profit >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmt(summary.net_profit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="fin-margin">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Percent className="w-3.5 h-3.5 text-blue-600" />
                <p className="text-xs text-stone-500">Profit Margin</p>
              </div>
              <p className="text-xl font-semibold text-stone-900">{summary.profit_margin}%</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="fin-labor-pct">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Percent className="w-3.5 h-3.5 text-amber-600" />
                <p className="text-xs text-stone-500">Labor Cost %</p>
              </div>
              <p className="text-xl font-semibold text-stone-900">{summary.labor_pct}%</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="fin-cogs-pct">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Percent className="w-3.5 h-3.5 text-stone-500" />
                <p className="text-xs text-stone-500">COGS %</p>
              </div>
              <p className="text-xl font-semibold text-stone-900">{summary.cogs_pct}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Weekly Trend */}
      {summary?.recent_trend?.length > 0 && (
        <div className="mb-8">
          <h3 className="text-base font-semibold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Weekly Trend</h3>
          <div className="bg-white border border-stone-200/60 rounded-xl p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="flex items-end gap-2 h-32">
              {summary.recent_trend.map((w, i) => {
                const maxVal = Math.max(...summary.recent_trend.map(t => Math.max(t.revenue, t.projected)));
                const revH = maxVal > 0 ? (w.revenue / maxVal) * 100 : 0;
                const projH = maxVal > 0 ? (w.projected / maxVal) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="flex gap-0.5 items-end w-full h-24">
                      <div className="flex-1 bg-emerald-500 rounded-t transition-all" style={{ height: `${revH}%`, minHeight: revH > 0 ? "4px" : "0" }} title={`Revenue: ${fmt(w.revenue)}`} />
                      <div className="flex-1 bg-stone-200 rounded-t transition-all" style={{ height: `${projH}%`, minHeight: projH > 0 ? "4px" : "0" }} title={`Projected: ${fmt(w.projected)}`} />
                    </div>
                    <span className="text-[10px] text-stone-400">{w.week.slice(5)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /> Actual</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-stone-200 rounded-sm" /> Projected</span>
            </div>
          </div>
        </div>
      )}

      {/* Entries Table */}
      {entries.length === 0 && !loading ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No financial data yet</h3>
          <p className="text-sm text-stone-500 mb-4">Start logging weekly revenue and expenses per store.</p>
          <Button data-testid="first-entry-btn" onClick={openNew} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-2" /> Log First Week
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry, idx) => {
            const exp = totalExpenses(entry);
            const profit = (entry.revenue || 0) - exp;
            return (
              <Card key={entry.id} data-testid={`fin-entry-${entry.id}`} className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl animate-slide-up" style={{ animationDelay: `${idx * 0.03}s` }}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 text-center flex-shrink-0">
                      <p className="text-xs text-stone-400">Week of</p>
                      <p className="text-sm font-semibold text-stone-900">{entry.week_start?.slice(5)}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900">{getStoreName(entry.store_id)}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-stone-500 flex-wrap">
                        <span>Rev: <span className="font-medium text-stone-700">{fmt(entry.revenue)}</span></span>
                        <span>Exp: <span className="font-medium text-stone-700">{fmt(exp)}</span></span>
                        <span>COGS: {fmt(entry.cogs)}</span>
                        <span>Rent: {fmt(entry.rent)}</span>
                        <span>Labor: {fmt(entry.labor_cost)}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-sm font-semibold ${profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(profit)}</p>
                      <p className="text-xs text-stone-400">profit</p>
                    </div>
                    {entry.projected_revenue > 0 && (
                      <div className="text-right flex-shrink-0 hidden md:block">
                        <p className={`text-xs ${entry.revenue >= entry.projected_revenue ? "text-emerald-600" : "text-red-500"}`}>
                          {entry.revenue >= entry.projected_revenue ? "On target" : "Below proj"}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-1 flex-shrink-0">
                      <Button data-testid={`edit-fin-${entry.id}`} variant="ghost" size="sm" onClick={() => openEdit(entry)} className="text-stone-400 hover:text-stone-700 px-2"><Edit className="w-3.5 h-3.5" /></Button>
                      <Button data-testid={`delete-fin-${entry.id}`} variant="ghost" size="sm" onClick={() => deleteEntry(entry.id)} className="text-stone-400 hover:text-red-600 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}
